// Вид проектировщика (ГИП): очередь замечаний на ответ + панель ответа.
// Фаза 10 (2026-07-04): убраны мок-сабтабы «Документы»/«Зона влияния» и
// ВЫДУМАННЫЕ сроки (синтезировались из важности). Срочность — от РЕАЛЬНОГО
// проектного дедлайна (project.deadline_at); сортировка задач — по важности.
import { useState, useMemo } from 'react';
import { Inbox, CheckCircle2, CalendarClock } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Spinner } from './Spinner';
import { EmptyState } from './EmptyState';
import { RemarkDetail } from './RemarkDetail';
import type { Remark, RemarksDashboardResponse } from '@/types/api';
import { normSeverity } from '@/types/api';

const SEV_WEIGHT: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
const SEV_COLOR: Record<string, string> = { critical: '#EF4444', high: '#F59E0B', medium: '#6B7280', low: '#9CA3AF' };
const SEV_LABEL: Record<string, string> = { critical: 'Критическое', high: 'Высокое', medium: 'Среднее', low: 'Низкое' };

// Статус замечания (выводится бэкендом): open — ждёт ответа ГИП;
// awaiting_response — ГИП ответил, на проверке у заказчика; closed — закрыто.
type TaskFilter = 'todo' | 'review' | 'all';

function remarkStatus(r: Remark): 'open' | 'awaiting_response' | 'closed' {
  return (r.remark_status as 'open' | 'awaiting_response' | 'closed')
    || (r.closed_at ? 'closed' : 'open');
}

interface Props {
  projectId: string;
  deadlineAt?: string | null;   // project.deadline_at (реальный проектный срок)
  onGoToDocs?: () => void;       // переход к загрузке исправленной документации
}

export function GIPSplitView({ projectId, deadlineAt, onGoToDocs }: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<TaskFilter>('todo');

  const remarksQ = useQuery({
    queryKey: ['remarks-dashboard', projectId],
    queryFn: () => api.get<RemarksDashboardResponse>(`/projects/${projectId}/remarks/dashboard`),
  });

  const all = useMemo(() => {
    const rows = (remarksQ.data?.remarks || []).map((r) => ({ ...r, _st: remarkStatus(r) }));
    // Сортировка по важности (без выдуманных сроков): критические — выше.
    rows.sort((a, b) => (SEV_WEIGHT[normSeverity(b.severity)] || 0) - (SEV_WEIGHT[normSeverity(a.severity)] || 0));
    return rows;
  }, [remarksQ.data]);

  const todo = useMemo(() => all.filter((r) => r._st === 'open'), [all]);
  const review = useMemo(() => all.filter((r) => r._st === 'awaiting_response'), [all]);
  const visible = filter === 'todo' ? todo : filter === 'review' ? review : all;

  // Разбивка ВИДИМЫХ задач по типу (только для 'todo'/'all' — при 'review' группировка та же).
  const sTasks = useMemo(() => visible.filter((r) => r.defect_type === 'S-defect'), [visible]);
  const eTasks = useMemo(() => visible.filter((r) => r.defect_type === 'E-gap'), [visible]);
  const otherTasks = useMemo(() => visible.filter((r) => !['S-defect', 'E-gap'].includes(r.defect_type || '')), [visible]);

  const selected = useMemo(() => all.find((r) => r.id === selectedId) || null, [all, selectedId]);

  const daysLeft = calcDaysLeft(deadlineAt);

  return (
    <div className="grid grid-cols-12" style={{ gap: 24, minHeight: 'calc(100vh - 320px)' }}>
      {/* Left: очередь задач */}
      <div className="col-span-6">
        <div className="bg-white border border-border rounded-card flex flex-col" style={{ height: '100%', overflow: 'hidden' }}>
          {/* Проектный дедлайн — реальный контекст срочности (общий для всех задач). */}
          <div className="flex items-center border-b border-border" style={{ padding: '12px 16px', gap: 8 }}>
            <CalendarClock size={16} stroke={daysLeft === null ? '#6B7280' : daysLeft < 0 ? '#EF4444' : daysLeft <= 7 ? '#F59E0B' : '#10B981'} />
            <span className="text-ink" style={{ fontSize: 13, fontWeight: 600 }}>
              {daysLeft === null ? 'Срок сдачи не задан заказчиком'
                : daysLeft < 0 ? `Срок сдачи просрочен на ${Math.abs(daysLeft)} дн.`
                : daysLeft === 0 ? 'Срок сдачи — сегодня'
                : `До срока сдачи ${daysLeft} дн.`}
            </span>
          </div>

          {/* Фильтр по статусу */}
          <div className="flex border-b border-border" style={{ padding: '10px 16px 0', gap: 4 }}>
            {([
              { id: 'todo', label: 'Ждут ответа', count: todo.length },
              { id: 'review', label: 'На проверке', count: review.length },
              { id: 'all', label: 'Все', count: all.length },
            ] as const).map((t) => {
              const active = filter === t.id;
              return (
                <button key={t.id} type="button" onClick={() => setFilter(t.id)}
                  style={{ padding: '10px 14px', fontWeight: active ? 600 : 500, fontSize: 13,
                           color: active ? '#0A1628' : '#6B7280', background: 'transparent', border: 'none',
                           borderBottom: active ? '2px solid #F59E0B' : '2px solid transparent', marginBottom: -1, cursor: 'pointer' }}>
                  {t.label}<span className="ml-1.5 text-muted tabular" style={{ fontSize: 11 }}>{t.count}</span>
                </button>
              );
            })}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            {remarksQ.isLoading && <Spinner size={24} className="text-muted" />}
            {!remarksQ.isLoading && visible.length === 0 && (
              <EmptyState icon={CheckCircle2}
                title={filter === 'todo' ? 'Нет замечаний, ждущих ответа' : filter === 'review' ? 'Ничего не на проверке' : 'Замечаний нет'}
                body={filter === 'todo' ? 'Все замечания отработаны или на проверке у заказчика.' : undefined} />
            )}
            {sTasks.length > 0 && <TaskSection title="Блокирующие" subtitle="пересмотр решения" color="#EF4444" tasks={sTasks} selectedId={selectedId} onSelect={setSelectedId} />}
            {eTasks.length > 0 && <TaskSection title="Устранимые" subtitle="обновление документа" color="#F59E0B" tasks={eTasks} selectedId={selectedId} onSelect={setSelectedId} />}
            {otherTasks.length > 0 && <TaskSection title="Прочие" subtitle="без классификации" color="#6B7280" tasks={otherTasks} selectedId={selectedId} onSelect={setSelectedId} />}
          </div>
        </div>
      </div>

      {/* Right: RemarkDetail (ответ + загрузка исправления) */}
      <div className="col-span-6">
        {selected ? (
          <RemarkDetail remark={selected} projectId={projectId} role="gip" onClose={() => setSelectedId(null)} onGoToDocs={onGoToDocs} />
        ) : (
          <div className="bg-surface border border-dashed border-border rounded-card flex items-center justify-center text-center" style={{ minHeight: 400, padding: 32 }}>
            <div>
              <Inbox size={48} stroke="#6B7280" style={{ margin: '0 auto' }} />
              <h4 className="font-heading text-ink mt-4" style={{ fontSize: 18, fontWeight: 600 }}>Выберите замечание слева</h4>
              <p className="text-muted mt-2 mx-auto" style={{ fontSize: 13, maxWidth: 320 }}>
                Ответьте на замечание и при необходимости загрузите исправленную версию тома.
                Документацию целиком — на вкладке «Документы».
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function calcDaysLeft(deadlineAt: string | null | undefined): number | null {
  if (!deadlineAt) return null;
  try {
    const dl = new Date(deadlineAt);
    const now = new Date();
    return Math.ceil((dl.getTime() - now.getTime()) / 86400000);
  } catch { return null; }
}

function TaskSection({ title, subtitle, color, tasks, selectedId, onSelect }: {
  title: string; subtitle: string; color: string;
  tasks: Array<Remark & { _st: string }>;
  selectedId: string | null; onSelect: (id: string) => void;
}) {
  const STATUS_LABEL: Record<string, string> = { open: 'ждёт ответа', awaiting_response: 'на проверке', closed: 'закрыто' };
  return (
    <div style={{ marginBottom: 20 }}>
      <div className="flex items-center mb-2.5" style={{ gap: 10 }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
        <span className="font-heading text-ink" style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>
        <span className="rounded-pill bg-surface text-muted tabular" style={{ padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>{tasks.length}</span>
        <span className="text-muted ml-auto" style={{ fontSize: 11 }}>{subtitle}</span>
      </div>
      {tasks.map((r) => {
        const sev = normSeverity(r.severity);
        const sevColor = SEV_COLOR[sev];
        const isActive = selectedId === r.id;
        return (
          <button key={r.id} type="button" onClick={() => onSelect(r.id)}
            className="w-full text-left rounded-card relative overflow-hidden block mb-2"
            style={{ padding: '12px 14px 12px 18px', background: isActive ? 'rgba(245,158,11,.06)' : '#FFFFFF',
                     border: isActive ? '1px solid rgba(10,22,40,.4)' : '1px solid #E5E7EB', cursor: 'pointer' }}>
            <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: sevColor }} />
            <div className="flex items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: sevColor }}>{SEV_LABEL[sev]}</span>
              <span className="text-muted">·</span>
              <span style={{ fontSize: 11, fontWeight: 500, color: '#0A1628' }}>{r.section_code || 'без раздела'}</span>
              <span className="rounded-pill bg-surface text-muted ml-auto" style={{ padding: '1px 8px', fontSize: 10.5, fontWeight: 600 }}>
                {STATUS_LABEL[r._st] || r._st}
              </span>
            </div>
            <div className="text-ink mt-1.5" style={{ fontSize: 13, lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {r.body}
            </div>
          </button>
        );
      })}
    </div>
  );
}
